import React, { useMemo } from "react";
import { app, translate } from "../app";
import { languages } from "../locale";

type ArticleLite = {
  id?: number;
  title?: LocalizableText;
  description?: LocalizableText;
  updated_at?: string;
  section?: number;
  category?: number;
};

export function LatestArticles({ limit = 8 }: { limit?: number }) {
  const locale = languages[app.locale]?.zendesk?.toLowerCase() ?? app.locale.toLowerCase();

  const items = useMemo(() => {
    const all = Object.values(app.data.zendesk.articles || {}) as ArticleLite[];
    const currentLocale = app.locale;
    const accentColors = ['#FDF1B1', '#F9D79E', '#ABDFBF', '#C0E1E0', '#B1C3FB', '#CDA9E9'];
    const getAccent = (catId?: number) => {
      if (!catId && catId !== 0) return '#e5e7eb';
      const idx = Math.abs(Number(catId)) % accentColors.length;
      return accentColors[idx];
    };
    const truncate = (s: string, n: number) =>
      (s || "").trim().length > n ? (s || "").trim().slice(0, n - 1).trimEnd() + "…" : (s || "").trim();

    const formatDate = (dateString?: string) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(app.locale, { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      } catch {
        return "";
      }
    };

    const sorted = all
      .slice()
      .sort(
        (a, b) =>
          (Date.parse(b.updated_at || "") || 0) - (Date.parse(a.updated_at || "") || 0)
      );
    // Keep only articles that have a title in the current language (no fallback)
    const translated = sorted.filter((a) => {
      const titleObj: any = (a as any).name || a.title || {};
      const raw = typeof titleObj === "object" ? titleObj?.[currentLocale] : titleObj;
      return !!(raw && String(raw).trim());
    }).slice(0, limit);
    return translated.map((a) => {
      const url = `/${locale}/articles/${a.id}`;
      // Some datasets store article title under `name` instead of `title`
      const displayTitle = translate((a as any).name || a.title || {});
      const title = truncate(displayTitle, 80);
      const categoryName = a.category
        ? translate(app.data.zendesk.categories?.[a.category]?.name || {})
        : "";
      const accent = getAccent(a.category);
      const dateAdded = formatDate(a.updated_at);
      return { id: a.id, url, title, category: categoryName, accent, dateAdded };
    });
  }, [app.data.zendesk.articles, locale, limit]);

  if (!items.length) return null;

  return (
    <section className="latest-articles">
      <div className="latest-header">
        <h3 className="latest-title">
          {translate({ ["en-US"]: "Latest articles", ["es-ES"]: "Artículos recientes", ["fr-FR"]: "Derniers articles", ["fr-HT"]: "Dènye atik yo" })}
        </h3>
      </div>
      <div className="latest-track" role="list">
        {items.map((item: any) => {
          return (
            <a
              key={item.id}
              className="latest-card"
              role="listitem"
              aria-label={item.title}
              href={item.url}
              style={{ ['--accent' as any]: item.accent }}
            >
              {item.category && <div className="latest-badge">{item.category}</div>}
              <div className="latest-card-title">{item.title}</div>
              {item.dateAdded && <div className="latest-card-meta">{item.dateAdded}</div>}
            </a>
          );
        })}
      </div>
    </section>
  );
}


